import { Dialog, Grid, Slide, useMediaQuery } from "@mui/material"
import Box from "@mui/material/Box"
import Paper from "@mui/material/Paper"
import SpeedDial from "@mui/material/SpeedDial"
import SpeedDialAction from "@mui/material/SpeedDialAction"
import SpeedDialIcon from "@mui/material/SpeedDialIcon"
import { DataGrid } from "@mui/x-data-grid"
import dayjs from "dayjs"
import { getAnalytics } from "firebase/analytics"
import {
  collection,
  DocumentData,
  getFirestore,
  onSnapshot,
  orderBy,
  query,
  QueryDocumentSnapshot,
} from "firebase/firestore"
import { FunctionComponent, useEffect, useState } from "react"
import { FormattedMessage, useIntl } from "react-intl"
import Color from "../Color"
import { getBuyerFromDocSnapshot } from "../contexts/BuyerContext"
import Dimen from "../Dimen"
import { Collection, Field } from "../Enum"
import {
  paperStyleDefault,
  redGreenCellColor,
  useSpeedDialStyle,
} from "../Style"
import { timber_debug } from "../Timber"
import { Buyer } from "../Type"
import {
  getBuyerListColumns,
  getTranslatedDataGridToolbar,
} from "../utils/DataGridUtils"
import { exportBuyersAsExcel } from "../utils/ExcelUtils"
import { exportBuyerListAsPdf } from "../utils/PdfUtils"
import { Action, getBuyerListSpeedDialActions } from "../utils/SpeedDialUtils"
import BuyerDialog from "./BuyerDialog"

const emptyBuyer: Buyer = {
  id: "",
  name: "",
  address: "",
  phone: "",
  email: "",
  website: "",
  insertDate: dayjs().valueOf(),
  lastEditDate: dayjs().valueOf(),
}

const BuyerList: FunctionComponent<{ userId: string }> = ({ userId }) => {
  const intl = useIntl()
  const classes = useSpeedDialStyle()
  const minHeight = useMediaQuery("(min-height:700px)")
  const analytics = getAnalytics()

  const [buyers, setBuyers] = useState<Buyer[]>([])
  const [showBuyerDialog, setShowBuyerDialog] = useState<boolean>(false)
  const [buyer, setBuyer] = useState<Buyer>(emptyBuyer)

  // Firestore
  const firestore = getFirestore()
  const buyersRef = collection(
    firestore,
    `/${Collection.Users}/${userId}/${Collection.Buyers}`
  )
  const buyersQuery = query(buyersRef, orderBy(Field.Name))

  useEffect(() => {
    // Sets up real-time buyers listener
    timber_debug("useEffect subscribeBuyersListener")
    const unsubscribeBuyersListener = onSnapshot(
      buyersQuery,
      (querySnapshot) => {
        const newBuyers: Buyer[] = []
        querySnapshot.forEach(
          (doc: QueryDocumentSnapshot<DocumentData, DocumentData>) => {
            newBuyers.push(getBuyerFromDocSnapshot(doc))
          }
        )
        timber_debug(`Downloaded ${newBuyers.length} buyers`)
        setBuyers(newBuyers)
      },
      (error) => {
        console.error("Error fetching buyers", error)
      }
    )
    return () => {
      timber_debug("useEffect unsubscribeBuyersListener")
      unsubscribeBuyersListener()
    }
  }, [])

  const onSelectBuyer = (buyer: Buyer) => {
    setBuyer(buyer)
    setShowBuyerDialog(true)
  }

  const openBuyerDialog = () => {
    // Reset buyer before opening dialog
    setBuyer(emptyBuyer)
    setShowBuyerDialog(true)
  }

  const handleFabAction = (actionId: string) => {
    switch (actionId) {
      case "buyer":
        openBuyerDialog()
        break
      case "pdf":
        exportBuyerListAsPdf(buyers, analytics)
        break
      case "excel":
        exportBuyersAsExcel(buyers, analytics)
        break
      default:
        break
    }
  }

  return (
    <Box
      height="91vh"
      display="flex"
      flexDirection="column"
      sx={redGreenCellColor}
    >
      <Paper
        elevation={3}
        style={{
          height: "85vh",
          margin: "8px",
          ...paperStyleDefault,
          padding: "8px",
        }}
      >
        <div style={{ fontSize: Dimen.FontSizeDefault }}>
          <FormattedMessage id="nav.buyer.list" />
        </div>
        <Grid
          container
          spacing={minHeight ? 1 : 0}
          direction={"row"}
          height="85vh"
          marginTop={1}
        >
          <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
            {/* Buyer list */}
            <div style={{ height: minHeight ? "85%" : "100%" }}>
              <DataGrid
                localeText={getTranslatedDataGridToolbar(intl)}
                onRowClick={(params) => onSelectBuyer(params.row as Buyer)}
                rows={buyers}
                columns={getBuyerListColumns(intl)}
                pageSizeOptions={[10]}
                disableRowSelectionOnClick
                density="standard"
                showToolbar
              />
            </div>
          </Grid>
        </Grid>

        {/* Action button */}
        <SpeedDial
          ariaLabel="BuyerList SpeedDial"
          sx={{
            position: "absolute",
            bottom: 16,
            right: 16,
          }}
          icon={<SpeedDialIcon />}
          FabProps={{
            style: { backgroundColor: Color.Red40, color: Color.White },
          }}
        >
          {getBuyerListSpeedDialActions(intl).map((action: Action) => (
            <SpeedDialAction
              key={action.id}
              icon={action.icon}
              slotProps={{
                tooltip: {
                  title: action.name,
                  open: true,
                },
                fab: {
                  style: {
                    backgroundColor: Color.Primary,
                    color: Color.Black,
                  },
                },
              }}
              classes={classes} // for keep action tooltip text in a single line
              onClick={() => {
                handleFabAction(action.id)
              }}
            />
          ))}
        </SpeedDial>

        {/* Buyer dialog */}
        <Dialog
          open={showBuyerDialog}
          slots={{ transition: Slide }}
          slotProps={{
            transition: {
              direction: "up",
            },
          }}
          keepMounted
          onClose={(_, reason) => {
            if (reason === "backdropClick" || reason === "escapeKeyDown") {
              setShowBuyerDialog(false)
            }
          }}
        >
          <BuyerDialog
            userId={userId}
            buyer={buyer}
            onClickClose={() => {
              setShowBuyerDialog(false)
            }}
          ></BuyerDialog>
        </Dialog>
      </Paper>
    </Box>
  )
}

export default BuyerList
